/* Free Gift With Purchase — storefront behavior.
 *
 * Responsibilities:
 *  1. Keep all free-gift CTA blocks on the page in sync with the live cart.
 *  2. Render a popup picker for the eligible-gift collection. Customers
 *     never leave the cart context.
 *  3. Enforce: one free gift per order and auto-remove when subtotal drops
 *     below the threshold (both configurable in theme settings).
 *
 * Real-time updates: window.fetch and XMLHttpRequest are wrapped so that
 * ANY cart-mutating request (from any code path — the theme's own ajax,
 * hulkapps, third-party apps, etc.) triggers a refresh.
 *
 * All settings come from data-* attributes on the rendered CTA snippet, so
 * this file has no hardcoded thresholds or copy.
 */
(function () {
  'use strict';

  var FG_PROP_KEY = '_free_gift';
  var FG_PROP_VAL = 'true';
  var CART_URL_RE = /\/cart(\/|\.js|\b)/i;
  var settingsCache = null;
  var enforcing = false;
  var refreshScheduled = false;
  var modalEl = null;
  var modalContentLoaded = false;

  function moneyFormat(cents) {
    var dollars = (cents / 100);
    if (dollars % 1 === 0) return '$' + dollars.toFixed(0);
    return '$' + dollars.toFixed(2);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function readSettings() {
    if (settingsCache) return settingsCache;
    var any = document.querySelector('[data-free-gift-cta]');
    if (!any) return null;
    settingsCache = {
      thresholdCents: parseInt(any.dataset.thresholdCents, 10) || 0,
      collectionUrl: any.dataset.collectionUrl || '/',
      autoRemove: any.dataset.autoRemove === 'true',
      onePerOrder: any.dataset.onePerOrder === 'true',
      unlockedCta: any.dataset.unlockedCta || 'Choose your free gift',
      progressTemplate: any.dataset.progressTemplate || 'Spend {{ amount }} more to unlock a FREE gift!',
      selectedBadge: any.dataset.selectedBadge || 'Selected as your free gift'
    };
    return settingsCache;
  }

  function findGiftLines(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (line) {
      return line.properties && line.properties[FG_PROP_KEY] === FG_PROP_VAL;
    });
  }

  function subtotalExcludingGift(cart) {
    if (!cart) return 0;
    var giftTotal = 0;
    findGiftLines(cart).forEach(function (line) {
      giftTotal += line.final_line_price || line.line_price || 0;
    });
    return (cart.total_price || 0) - giftTotal;
  }

  function fetchCart() {
    return originalFetch.call(window, '/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function changeCart(lineKey, quantity) {
    return originalFetch.call(window, '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  function addGiftToCart(variantId) {
    var properties = {};
    properties[FG_PROP_KEY] = FG_PROP_VAL;
    var formData = { items: [{ id: variantId, quantity: 1, properties: properties }] };
    return originalFetch.call(window, '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(formData)
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok) {
          var msg = (json && (json.description || json.message)) || 'Could not add the free gift.';
          throw new Error(msg);
        }
        // The `items: [...]` add-form returns { items: [line_item] }.
        // Unwrap to a single line_item so addToCartHandler.addItem can use it.
        if (json && Array.isArray(json.items) && json.items.length) {
          return json.items[0];
        }
        return json;
      });
    });
  }

  // ----- CTA sync ---------------------------------------------------------

  function renderProgress(wrapper, cart, opts) {
    var subtotal = subtotalExcludingGift(cart);
    var remaining = Math.max(opts.thresholdCents - subtotal, 0);
    var pct = Math.min((subtotal / opts.thresholdCents) * 100, 100);
    var unlocked = subtotal >= opts.thresholdCents;
    var giftLines = findGiftLines(cart);
    var hasSelection = giftLines.length > 0;

    var inner;
    if (unlocked && hasSelection) {
      var line = giftLines[0];
      var thumb = line.image
        ? '<img src="' + line.image + '" alt="" class="free-gift-cta__thumb" loading="lazy" width="48" height="48">'
        : '';
      inner =
        '<div class="free-gift-cta free-gift-cta--selected" data-state="selected">' +
          thumb +
          '<div class="free-gift-cta__body">' +
            '<div class="free-gift-cta__label">' + escapeHtml(opts.selectedBadge) + '</div>' +
            '<div class="free-gift-cta__title">' + escapeHtml(line.product_title || line.title || '') + '</div>' +
          '</div>' +
          '<button type="button" class="free-gift-cta__change" data-fg-open-modal>Change</button>' +
        '</div>';
    } else if (unlocked) {
      inner =
        '<button type="button" class="free-gift-cta free-gift-cta--unlocked" data-state="unlocked" data-fg-open-modal>' +
          '<span class="free-gift-cta__label">' + escapeHtml(opts.unlockedCta) + '</span>' +
          '<span class="free-gift-cta__chev" aria-hidden="true">›</span>' +
        '</button>';
    } else {
      var amountStr = moneyFormat(remaining);
      var text = opts.progressTemplate.replace(/\{\{\s*amount\s*\}\}/g, amountStr);
      inner =
        '<div class="free-gift-cta free-gift-cta--progress" data-state="progress">' +
          '<div class="free-gift-cta__text">' +
            '<span data-fg-progress-text>' + escapeHtml(text) + '</span>' +
          '</div>' +
          '<div class="free-gift-cta__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + Math.round(pct) + '">' +
            '<div class="free-gift-cta__bar-fill" data-fg-progress-fill style="width: ' + pct + '%;"></div>' +
          '</div>' +
        '</div>';
    }
    wrapper.innerHTML = inner;
  }

  function syncAll(cart) {
    var opts = readSettings();
    if (!opts) return;
    var wrappers = document.querySelectorAll('[data-free-gift-cta]');
    wrappers.forEach(function (w) { renderProgress(w, cart, opts); });
  }

  // ----- Enforcement ------------------------------------------------------

  function enforce(cart) {
    if (enforcing) return Promise.resolve(cart);
    var opts = readSettings();
    if (!opts) return Promise.resolve(cart);

    var giftLines = findGiftLines(cart);
    if (giftLines.length === 0) return Promise.resolve(cart);

    var subtotalNoGift = subtotalExcludingGift(cart);

    if (opts.autoRemove && subtotalNoGift < opts.thresholdCents) {
      enforcing = true;
      var removedVariantIds = giftLines.map(function (l) { return l.variant_id; });
      var p = Promise.resolve();
      giftLines.forEach(function (line) {
        p = p.then(function () { return changeCart(line.key, 0); });
      });
      return p.then(function () {
        enforcing = false;
        applyThemeCartDomUpdate(null, removedVariantIds);
        try {
          window.dispatchEvent(new CustomEvent('free-gift:removed', { detail: { reason: 'below-threshold' } }));
        } catch (e) {}
        return fetchCart();
      });
    }

    if (opts.onePerOrder && giftLines.length > 1) {
      enforcing = true;
      var p2 = Promise.resolve();
      giftLines.slice(1).forEach(function (line) {
        p2 = p2.then(function () { return changeCart(line.key, 0); });
      });
      return p2.then(function () {
        enforcing = false;
        return fetchCart();
      });
    }

    var overQty = giftLines.find(function (l) { return l.quantity > 1; });
    if (overQty) {
      enforcing = true;
      return changeCart(overQty.key, 1).then(function () {
        enforcing = false;
        return fetchCart();
      });
    }

    return Promise.resolve(cart);
  }

  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    setTimeout(function () {
      refreshScheduled = false;
      refresh();
    }, 120);
  }

  function refresh() {
    fetchCart().then(function (cart) {
      if (!cart) return;
      enforce(cart).then(function (finalCart) {
        var c = finalCart || cart;
        syncAll(c);
        updateModalSelectedState(c);
        updateGiftLinePrices(c);
      });
    });
  }

  // When a customer drops their subtotal below the threshold, the Shopify
  // automatic discount stops applying and the gift line's `final_price`
  // reverts to the variant price. The theme renders the cart drawer line
  // once at add-time and never re-paints existing lines on subsequent cart
  // changes, so we patch the price node here using the live cart data.
  function updateGiftLinePrices(cart) {
    if (!cart || !cart.items) return;
    var freeLabel = window.FG_FREE_LABEL || 'FREE';
    cart.items.forEach(function (line) {
      if (!line.properties || line.properties[FG_PROP_KEY] !== FG_PROP_VAL) return;

      var deleteLinks = document.querySelectorAll(
        '.tt-cart-list a[href*="id=' + line.variant_id + '"]'
      );
      deleteLinks.forEach(function (a) {
        var item = a.closest('.tt-item');
        if (!item) return;
        var priceEl = item.querySelector('.tt-price');
        if (!priceEl) return;

        // Discount is active when final per-unit price is below variant price.
        var discounted = (line.final_price != null && line.price != null) &&
                         (line.final_price < line.price);
        var origStr = moneyFormat(line.price);
        var finalStr = line.final_price === 0
          ? freeLabel
          : moneyFormat(line.final_price);

        if (discounted) {
          priceEl.innerHTML =
            '<span class="old-price">' + escapeHtml(origStr) + '</span> ' +
            '<span class="new-price">' + escapeHtml(finalStr) + '</span>';
        } else {
          priceEl.textContent = origStr;
        }
      });
    });
  }

  // ----- Modal: gift picker ----------------------------------------------

  function ensureModal() {
    if (modalEl) return modalEl;
    modalEl = document.createElement('div');
    modalEl.className = 'fg-modal';
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-label', 'Choose your free gift');
    modalEl.innerHTML =
      '<div class="fg-modal__backdrop" data-fg-close></div>' +
      '<div class="fg-modal__dialog" role="document">' +
        '<div class="fg-modal__header">' +
          '<button type="button" class="fg-modal__close" data-fg-close aria-label="Close">×</button>' +
          '<h2 class="fg-modal__title">Choose your FREE gift</h2>' +
          '<p class="fg-modal__subtitle" data-fg-modal-subtitle>Pick one item — it\'s on us.</p>' +
        '</div>' +
        '<div class="fg-modal__body" data-fg-modal-body>' +
          '<div class="fg-modal__loading">Loading gifts…</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modalEl);

    modalEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-fg-close]')) {
        closeModal();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modalEl.classList.contains('is-open')) closeModal();
    });
    return modalEl;
  }

  function openModal() {
    var modal = ensureModal();
    // Close the ATC popup if it's open — they're competing for attention.
    try {
      if (window.jQuery && window.jQuery('#modalAddToCartProduct').length) {
        window.jQuery('#modalAddToCartProduct').modal('hide');
      }
    } catch (e) {}
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('fg-modal-open');
    document.body.classList.add('fg-modal-open');

    if (!modalContentLoaded) {
      loadModalContent();
    } else {
      // Refresh to reflect currently-selected gift.
      fetchCart().then(function (cart) {
        if (cart) updateModalSelectedState(cart);
      });
    }

    // Focus the close button for accessibility.
    setTimeout(function () {
      var c = modal.querySelector('.fg-modal__close');
      if (c) c.focus();
    }, 50);
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('fg-modal-open');
    document.body.classList.remove('fg-modal-open');
  }

  function loadModalContent() {
    var body = modalEl.querySelector('[data-fg-modal-body]');

    originalFetch.call(window, '/?section_id=free-gift-picker', {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        // Section Rendering API wraps the section in a shopify-section div.
        // Strip that wrapper if present.
        var temp = document.createElement('div');
        temp.innerHTML = html.trim();
        var picker = temp.querySelector('.fg-picker') || temp.firstElementChild || temp;
        body.innerHTML = '';
        body.appendChild(picker);
        modalContentLoaded = true;
        wireModalProductCards(body);
        fetchCart().then(function (cart) {
          if (cart) updateModalSelectedState(cart);
        });
      })
      .catch(function (err) {
        body.innerHTML =
          '<div class="fg-modal__error">' +
            '<p>Sorry — we couldn\'t load the gift options. Please try again.</p>' +
            '<p class="fg-modal__error-detail">' + escapeHtml(err.message || '') + '</p>' +
          '</div>';
      });
  }

  function wireModalProductCards(root) {
    var cards = root.querySelectorAll('.fg-picker__card');
    cards.forEach(function (card) {
      var select = card.querySelector('[data-fg-variant-select]');
      var addBtn = card.querySelector('[data-fg-add]');
      var hiddenVariant = card.querySelector('[data-fg-variant-id]');

      if (select) {
        select.addEventListener('change', function () {
          if (this.value) {
            addBtn.disabled = false;
            addBtn.dataset.variantId = this.value;
          } else {
            addBtn.disabled = true;
            delete addBtn.dataset.variantId;
          }
        });
      } else if (hiddenVariant && !addBtn.dataset.variantId) {
        addBtn.dataset.variantId = hiddenVariant.value;
      }

      if (addBtn) {
        addBtn.addEventListener('click', function () { handleModalAdd(card, addBtn); });
      }
    });
  }

  function handleModalAdd(card, btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) {
      var select = card.querySelector('[data-fg-variant-select]');
      if (select) { select.focus(); select.classList.add('fg-picker__variant--error'); }
      return;
    }

    var opts = readSettings();
    if (!opts) return;

    fetchCart().then(function (cart) {
      var subtotalNoGift = subtotalExcludingGift(cart);
      if (subtotalNoGift < opts.thresholdCents) {
        var short = (opts.thresholdCents - subtotalNoGift) / 100;
        alert('Your cart is below the threshold. Add $' + short.toFixed(2) + ' more to claim your free gift.');
        closeModal();
        return;
      }

      var existing = findGiftLines(cart);
      var removedVariantIds = [];
      var swap = Promise.resolve();
      if (existing.length && opts.onePerOrder) {
        existing.forEach(function (line) {
          removedVariantIds.push(line.variant_id);
          swap = swap.then(function () { return changeCart(line.key, 0); });
        });
      }

      // Disable all add buttons while processing.
      var allBtns = modalEl.querySelectorAll('[data-fg-add]');
      allBtns.forEach(function (b) { b.disabled = true; });
      btn.classList.add('is-loading');
      var originalLabel = btn.textContent;
      btn.textContent = 'Adding…';

      swap
        .then(function () { return addGiftToCart(variantId); })
        .then(function (addedLineItem) {
          applyThemeCartDomUpdate(addedLineItem, removedVariantIds);
          btn.classList.remove('is-loading');
          btn.classList.add('fg-picker__add--selected');
          btn.textContent = 'Selected';
          // Brief pause so the user sees confirmation, then close.
          setTimeout(function () {
            closeModal();
            refresh();
          }, 600);
        })
        .catch(function (err) {
          allBtns.forEach(function (b) { b.disabled = false; });
          btn.classList.remove('is-loading');
          btn.textContent = originalLabel;
          alert(err && err.message ? err.message : 'Could not add the free gift.');
        });
    });
  }

  // Hook into the Wokiee/HFB theme's cart-drawer DOM mutators so the new gift
  // appears (and the old one disappears) without a page reload. The theme
  // never sees our fetch calls because we bypass jQuery.ajax / Shopify.addItem.
  function applyThemeCartDomUpdate(addedLineItem, removedVariantIds) {
    // 1. Remove old gift lines from the cart drawer DOM.
    if (removedVariantIds && removedVariantIds.length) {
      removedVariantIds.forEach(function (vid) {
        if (!vid) return;
        // Drawer: anchor's href contains `id=<variant_id>` (theme's delete link).
        var selectors = [
          'header .tt-cart-list a[href*="id=' + vid + '"]',
          '.tt-cart-list a[href*="id=' + vid + '"]'
        ];
        selectors.forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (a) {
            var item = a.closest('.tt-item');
            if (item) item.remove();
          });
        });
      });
    }

    // 2. Add the new gift via the theme's own DOM constructor.
    try {
      if (addedLineItem && window.addToCartHandler &&
          typeof window.addToCartHandler.addItem === 'function') {
        window.addToCartHandler.addItem(addedLineItem);
      }
    } catch (e) { /* defensive — never block the user flow */ }

    // 3. Update badge count + drawer totals using the theme's helper.
    fetchCart().then(function (cart) {
      if (!cart) return;
      try {
        if (window.addToCartHandler &&
            typeof window.addToCartHandler.updateGeneralInfo === 'function') {
          window.addToCartHandler.updateGeneralInfo(cart);
        }
      } catch (e) {}
      // Let any other cart-aware widgets re-render (subtotal calcs, etc.).
      try {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (e) {}
      // If we're on /cart, the line items live in the page body, not the
      // header drawer. The drawer hook above won't touch them — reload so
      // the customer sees a consistent state.
      if (/^\/cart(\/|$)/.test(window.location.pathname)) {
        setTimeout(function () { window.location.reload(); }, 700);
      }
    });
  }

  function updateModalSelectedState(cart) {
    if (!modalEl) return;
    var giftLines = findGiftLines(cart);
    var selectedProductIds = giftLines.map(function (l) { return String(l.product_id); });
    var selectedVariantIds = giftLines.map(function (l) { return String(l.variant_id); });

    modalEl.querySelectorAll('.fg-picker__card').forEach(function (card) {
      var pid = card.dataset.fgProductId;
      var overlay = card.querySelector('[data-fg-selected-overlay]');
      var addBtn = card.querySelector('[data-fg-add]');
      var isSelected = selectedProductIds.indexOf(String(pid)) !== -1;

      card.classList.toggle('fg-picker__card--selected', isSelected);
      if (overlay) overlay.hidden = !isSelected;

      if (addBtn) {
        var variantSel = card.querySelector('[data-fg-variant-select]');
        var needsVariant = variantSel && !variantSel.value;
        var thisVariantSelected = isSelected && (
          !variantSel || selectedVariantIds.indexOf(String(variantSel.value)) !== -1
        );
        if (thisVariantSelected) {
          addBtn.disabled = true;
          addBtn.classList.add('fg-picker__add--selected');
          addBtn.textContent = 'Selected';
        } else {
          addBtn.classList.remove('fg-picker__add--selected');
          addBtn.disabled = needsVariant;
          addBtn.textContent = window.FG_ADD_BUTTON || 'Add as free gift';
        }
      }
    });
  }

  // ----- Real-time sync: wrap fetch + XHR --------------------------------

  var originalFetch = window.fetch ? window.fetch.bind(window) : null;
  if (window.fetch) {
    window.fetch = function (resource, init) {
      var url = '';
      try {
        url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      } catch (e) {}
      var p = originalFetch.apply(window, arguments);
      if (url && CART_URL_RE.test(url) && url.indexOf('/cart.js') === -1) {
        p.then(function () { scheduleRefresh(); }, function () {});
      }
      return p;
    };
  }

  var origXhrOpen = XMLHttpRequest.prototype.open;
  var origXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { this.__fgUrl = url; } catch (e) {}
    return origXhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    var url = xhr.__fgUrl || '';
    if (url && CART_URL_RE.test(url) && url.indexOf('/cart.js') === -1) {
      xhr.addEventListener('loadend', function () { scheduleRefresh(); });
    }
    return origXhrSend.apply(this, arguments);
  };

  // ----- Wire-up ---------------------------------------------------------

  function onReady() {
    refresh();

    // Global click delegate for any "open gift picker" button.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-fg-open-modal]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      }
    });

    document.addEventListener('cart:updated', scheduleRefresh);
    document.addEventListener('cart:refresh', scheduleRefresh);
    document.addEventListener('shopify:section:load', scheduleRefresh);

    // Catch async-rendered modals / drawers that re-inject the CTA snippet.
    var mo = new MutationObserver(function (muts) {
      var needs = false;
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if ((n.matches && n.matches('[data-free-gift-cta]')) ||
              (n.querySelector && n.querySelector('[data-free-gift-cta]'))) {
            needs = true;
          }
        });
      });
      if (needs) scheduleRefresh();
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  window.FreeGift = {
    refresh: refresh,
    open: openModal,
    close: closeModal,
    fetchCart: fetchCart
  };
})();
